// pages/strategic-playbook/tectonic-shifts.js
// ════════════════════════════════════════════════════════════════════
// Tectonic shifts index page (F13 — split by category).
// Active shifts first (the ones being navigated now), Historical shifts
// second (the analog library), Emerging third (early signals).
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { listTectonicShifts } from '../../data/strategic-playbook/tectonic-shifts';

export async function getStaticProps() {
  return { props: { shifts: listTectonicShifts() } };
}

const CATEGORY_INTRO = {
  active: {
    eyebrow: '§ Active — navigating now',
    title: 'Shifts in motion.',
    body: 'Multi-year structural forces shaping the strategic landscape right now. Each tracked through stages, beneficiaries, watch-for signals, and (where authored) named positions and quantitative thresholds.',
  },
  historical: {
    eyebrow: '§ Historical — the analog library',
    title: 'Resolved shifts whose lessons inform the active ones.',
    body: 'Outcomes are known. Each historical shift carries extracted lessons for analog application. Active shifts link to historical shifts they resemble — the wedge between them is where the analog\'s power lives.',
  },
  emerging: {
    eyebrow: '§ Emerging — early signals',
    title: 'Early candidates not yet tectonic.',
    body: 'Forces showing structural-shift signature but pre-acceleration. Worth tracking; not yet in the active set.',
  },
};

export default function TectonicShiftsIndex({ shifts }) {
  const byCategory = {
    active: shifts.filter((s) => s.category === 'active'),
    historical: shifts.filter((s) => s.category === 'historical'),
    emerging: shifts.filter((s) => s.category === 'emerging'),
  };

  const totals = {
    all: shifts.length,
    active: byCategory.active.length,
    historical: byCategory.historical.length,
    emerging: byCategory.emerging.length,
  };

  return (
    <div className="sp-root">
      <Head>
        <title>Tectonic Shifts · Strategic Playbook · Shift</title>
        <meta
          name="description"
          content="The macro-frame model. Active shifts being navigated now + historical shifts as analog library."
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/strategic-playbook" className="finding-back">← Strategic Playbook</Link>
          <div className="findings-index-topbar-meta">
            Strategic Playbook · Tectonic Shifts
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">§ Macro frame</div>
          <h1 className="findings-index-title">Tectonic Shifts.</h1>
          <p className="findings-index-sub">
            Multi-year structural forces shaping the strategic landscape.
            Three categories: <strong>Active</strong> (currently navigating),
            <strong> Historical</strong> (resolved; serve as analog library),
            <strong> Emerging</strong> (early signals). Active shifts link
            to historical analogs they resemble — the wedge is where the
            analog’s power lives.
          </p>
        </header>

        <section className="findings-index-stats">
          <Stat label="Total" value={totals.all} />
          <Stat label="Active" value={totals.active} />
          <Stat label="Historical" value={totals.historical} />
          <Stat label="Emerging" value={totals.emerging} />
        </section>

        {byCategory.active.length > 0 && (
          <CategorySection
            categoryKey="active"
            shifts={byCategory.active}
          />
        )}

        {byCategory.historical.length > 0 && (
          <CategorySection
            categoryKey="historical"
            shifts={byCategory.historical}
          />
        )}

        {byCategory.emerging.length > 0 && (
          <CategorySection
            categoryKey="emerging"
            shifts={byCategory.emerging}
          />
        )}

        <footer className="findings-index-footer">
          <p>
            Tectonic shifts are macro frames; <Link href="/findings" className="methodology-inline-link">Lab findings</Link> are
            the rigorously-tested mechanisms that live underneath them. The
            connection runs both ways: a shift surfaces the corpus that tests
            its mechanism; a finding gains macro context from the shift it
            sits inside.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="findings-index-stat">
      <div className="findings-index-stat-value">{value}</div>
      <div className="findings-index-stat-label">{label}</div>
    </div>
  );
}

function CategorySection({ categoryKey, shifts }) {
  const intro = CATEGORY_INTRO[categoryKey] || {};
  return (
    <section className="tectonic-category">
      <header className="tectonic-category-header">
        <div className="finding-eyebrow">{intro.eyebrow}</div>
        <h2 className="tectonic-category-title">{intro.title}</h2>
        {intro.body && <p className="tectonic-category-body">{intro.body}</p>}
      </header>

      <div className="tectonic-index-grid">
        {shifts.map((s) => (
          <ShiftCard key={s.id} shift={s} />
        ))}
      </div>
    </section>
  );
}

function ShiftCard({ shift }) {
  const findingsCount = shift.relatedFindings?.length || 0;
  const hasContrarian = !!shift.contrarianRead;
  const namedCount = (shift.namedBeneficiaries?.length || 0) + (shift.namedTrapped?.length || 0);
  const metricsCount = shift.watchMetrics?.length || 0;
  const lessonsCount = shift.lessons?.length || 0;

  return (
    <Link href={`/strategic-playbook/tectonic/${shift.slug}`} className="tectonic-index-card">
      <div className="tectonic-index-card-head">
        <h2 className="tectonic-index-card-name">{shift.name}</h2>
        <span className="tectonic-index-card-stage">{shift.stageLabel}</span>
      </div>

      {shift.era && (
        <div className="tectonic-index-card-era">{shift.era}</div>
      )}

      <div className="tectonic-index-card-stages">
        {shift.stages.map((stage, i) => {
          let cls = 'tectonic-index-card-stage-cell';
          if (i < shift.currentStageIndex) cls += ' passed';
          if (i === shift.currentStageIndex) cls += ' active';
          return <div key={stage} className={cls} />;
        })}
      </div>

      <p className="tectonic-index-card-note">{shift.note}</p>

      <div className="tectonic-index-card-foot">
        <span className="tectonic-index-card-foot-item">
          {shift.beneficiaries.length} beneficiaries
        </span>
        <span className="tectonic-index-card-foot-item">
          {shift.trappedSectors.length} trapped
        </span>
        {hasContrarian && (
          <span className="tectonic-index-card-foot-tag">Contrarian read ✓</span>
        )}
        {namedCount > 0 && (
          <span className="tectonic-index-card-foot-tag">{namedCount} named</span>
        )}
        {metricsCount > 0 && (
          <span className="tectonic-index-card-foot-tag">{metricsCount} metrics</span>
        )}
        {lessonsCount > 0 && (
          <span className="tectonic-index-card-foot-tag">{lessonsCount} lessons</span>
        )}
        {findingsCount > 0 && (
          <span className="tectonic-index-card-foot-findings">
            {findingsCount} finding{findingsCount === 1 ? '' : 's'} →
          </span>
        )}
      </div>
    </Link>
  );
}
